import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { emptyObject } from 'ui-components/default-props/empty';

import ErrorHeader from './error-header';
import { useCurrentUserId } from '../../../../../../hooks/use-current-user-id';
import { LogService } from '../../../../../../services/log-service';
import Global from '../../../../../../store/global-selectors';
import { ActiveClientProptype, InactiveClientProptype, PendingClientProptype } from '../../../../clients-3pl/prop-types';
import { usePaymentAndSubscriptionPageContext } from '../../../context/payment-and-subscription-page-context';
import { messages } from '../../../messages';
import style from '../../../style.module.less';
import threePLMessages from '../../3pl-provider-bundle/messages';

const cancelSubscriptionAndRemoveBundleViaSupportLink = 'https://survey.alchemer.com/s3/6627308/SS-3PL-APP-Cancellation-Form';

const CancelSubscriptionViaSupportModal = ({ clientData = emptyObject, onClose }) => {
  const { errorDuring3plPlanRemoval } = usePaymentAndSubscriptionPageContext();
  const currentUserId = useCurrentUserId();
  const sellerId = useSelector(Global.selectSellerSellerId);

  const onClick = useCallback(async () => {
    const now = new Date().toString();
    await LogService.logWarning(
      `UserId: ${currentUserId} of Seller ${sellerId} navigated to the "3PL-User-with-Connected-Clients Cancel ShipStation Subscription Support Form at ${now}"`,
    );
    onClose();
  }, [currentUserId, onClose, sellerId]);

  return (
    <Modal className={style['cancelation-support-modal']}>
      <ModalHeader>
        <FormattedMessage {...messages.cancelSubscriptionModalHeader} />
      </ModalHeader>
      <ModalBody>
        <ErrorHeader
          activeClientCount={clientData.activeClients?.length}
          didErrorFetchingClientData={clientData.errorFetchingClients}
          pendingClients={clientData.pendingClients}
          removalError={errorDuring3plPlanRemoval}
        />
        <div className={style['cancelation-support-modal-content']}>
          <FormattedMessage {...threePLMessages.cancelSubscriptionPlanViaSupport} />
        </div>
      </ModalBody>
      <ModalFooter className={style.footer}>
        <ModalFooterCancelButton />
        <Button href={cancelSubscriptionAndRemoveBundleViaSupportLink} onClick={onClick} theme='primary'>
          <FormattedMessage {...threePLMessages.cancelSubscriptionPlanViaSupportButton} />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

CancelSubscriptionViaSupportModal.propTypes = {
  clientData: PropTypes.shape({
    activeClients: PropTypes.arrayOf(ActiveClientProptype),
    inactiveClients: PropTypes.arrayOf(InactiveClientProptype),
    pendingClients: PropTypes.arrayOf(PendingClientProptype),
    errorFetchingClients: PropTypes.bool,
  }),
  onClose: PropTypes.func.isRequired,
};

export default CancelSubscriptionViaSupportModal;
